import {useEffect, useState, useSyncExternalStore} from 'react';
import dynamic from 'next/dynamic';

// Open/closed state of the site-wide contact form dialog, kept in a module-level store instead of
// `_app` state. Opening the form used to re-render the whole page (layout + every section) because
// the flag lived at the top of the tree — ~300 ms of work on a mid-range phone, on the tap that
// starts a lead (INP). Now only the components below re-render when it changes.

let isOpen = false;
const listeners = new Set<() => void>();

const setOpen = (next: boolean) => {
    if (isOpen === next) return;
    isOpen = next;
    listeners.forEach((listener) => listener());
};

/** Stable functions: the `openForm` prop every page and layout receives. */
export const openContactForm = () => setOpen(true);
export const closeContactForm = () => setOpen(false);

const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

const getOpen = () => isOpen;
const getServerOpen = () => false;

const useContactFormOpen = () => useSyncExternalStore(subscribe, getOpen, getServerOpen);

const ContactPageForm = dynamic(() => import('@/components/Ui-components/contact-page-form'), {ssr: false, loading: () => null});
const MUIDialog = dynamic(() => import('@/components/MUI-Components/MUI-Dialog'), {ssr: false, loading: () => null});
const PopupManager = dynamic(() => import('@/components/Ui-components/PopupManager'), {ssr: false, loading: () => null});

/** Runs `fn` after the browser has painted the current frame (rAF fires before the paint, the timeout after it). */
const afterNextPaint = (fn: () => void) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const frame = requestAnimationFrame(() => {
        timer = setTimeout(fn, 0);
    });
    return () => {
        cancelAnimationFrame(frame);
        if (timer !== undefined) clearTimeout(timer);
    };
};

const hiddenDialogSx = {display: 'none'} as const;

/**
 * The contact form dialog, mounted only while open (as before).
 *
 * Opening and closing are split across two frames so the tap itself only does light work (INP):
 * - open: the tap paints the backdrop and an empty card, and the form renders right after that paint. The card's
 *   Zoom transition holds it invisible for its first 100ms, so the form is in place before anything shows.
 * - close: the tap hides the dialog at once (as before it vanished at once) and the tree is unmounted after that paint.
 * The form itself is unchanged: created on open, destroyed on close, same fields, submission and tracking.
 */
export const ContactFormDialog = () => {
    const open = useContactFormOpen();
    const [formReady, setFormReady] = useState(false);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        if (open) {
            setMounted(true);
            return afterNextPaint(() => setFormReady(true));
        }
        return afterNextPaint(() => {
            setFormReady(false);
            setMounted(false);
        });
    }, [open]);

    if (!open && !mounted) return null;
    return (
        <MUIDialog
            open
            handleClose={closeContactForm}
            className="contact-page-form-dialog mui-dialog-video-overlay"
            fullWidth
            maxWidth="lg"
            sx={open ? undefined : hiddenDialogSx}
        >
            {formReady && <ContactPageForm onClose={closeContactForm} />}
        </MUIDialog>
    );
};

/** PopupManager, told whether the contact form is open so its pop-ups never stack on top of it. */
export const ContactFormAwarePopupManager = () => {
    const open = useContactFormOpen();
    return <PopupManager isFormOpen={open} />;
};
